import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowRight,
  Bot,
  Briefcase,
  Database,
  Layers,
  Sparkles,
  UserCog,
  Users,
  Activity,
} from "lucide-react";

import { client } from "@/utils/orpc";
import { Badge } from "@super-resume-new/ui/components/badge";
import { Button } from "@super-resume-new/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@super-resume-new/ui/components/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@super-resume-new/ui/components/table";
import { seo } from "@/utils/seo";

export const Route = createFileRoute("/admin/")({
  head: () =>
    seo({
      title: "Admin Dashboard | JobCues",
      description: "Overview metrics and system health for JobCues administrator console.",
      path: "/admin",
      noindex: true,
    }),
  component: AdminOverviewPage,
});

function AdminOverviewPage() {
  const { data: stats, isLoading } = useQuery({
    queryKey: ["admin", "stats"],
    queryFn: () => client.admin.getStats(),
  });

  return (
    <div className="space-y-6">
      {/* Metric Cards Grid */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {/* Metric 1 */}
        <Card className="border-border bg-card shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Total Users
            </CardTitle>
            <Users className="size-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold tabular-nums">
              {isLoading ? "…" : (stats?.users.total ?? 0)}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              <span className="font-semibold text-emerald-500">{stats?.users.admins ?? 0}</span>{" "}
              admins ·{" "}
              <span className="text-destructive font-semibold">{stats?.users.banned ?? 0}</span>{" "}
              banned
            </p>
          </CardContent>
        </Card>

        {/* Metric 2 */}
        <Card className="border-border bg-card shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Live Job Catalog
            </CardTitle>
            <Briefcase className="size-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold tabular-nums">
              {isLoading ? "…" : (stats?.jobs.active ?? 0)}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              <span className="font-semibold text-amber-500">{stats?.jobs.draft ?? 0}</span> drafts
              · <span className="text-muted-foreground">{stats?.jobs.expired ?? 0}</span> expired
            </p>
          </CardContent>
        </Card>

        {/* Metric 3 */}
        <Card className="border-border bg-card shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Staging Buffer Queue
            </CardTitle>
            <Database className="size-4 text-emerald-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold tabular-nums">
              {isLoading ? "…" : (stats?.staging.pending ?? 0)}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              <span className="text-amber-500 font-semibold">{stats?.staging.processing ?? 0}</span>{" "}
              processing ·{" "}
              <span className="text-destructive font-semibold">{stats?.staging.failed ?? 0}</span>{" "}
              failed
            </p>
          </CardContent>
        </Card>

        {/* Metric 4 */}
        <Card className="border-border bg-card shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Match Checks &amp; Reports
            </CardTitle>
            <Sparkles className="size-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold tabular-nums">
              {isLoading ? "…" : (stats?.matches ?? 0)}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              <span className="text-amber-500 font-semibold">{stats?.reports ?? 0}</span> open job
              reports
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Quick Launchpad */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="border-border bg-card/60 p-5 flex flex-col justify-between hover:border-brand/30 transition">
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold text-foreground mb-1">
              <Bot className="size-4 text-muted-foreground" />
              Automated Ingestion Pipeline
            </div>
            <p className="text-xs text-muted-foreground">
              Trigger live ATS scraping across Greenhouse, Lever, Ashby, and aggregators with 7-day
              freshness filtering.
            </p>
          </div>
          <Link to="/admin/ingestion" className="mt-4 w-fit">
            <Button size="sm" className="gap-1.5">
              Launch Pipeline <ArrowRight className="size-3.5" />
            </Button>
          </Link>
        </Card>

        <Card className="border-border bg-card/60 p-5 flex flex-col justify-between hover:border-border transition">
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold text-foreground mb-1">
              <Layers className="size-4 text-muted-foreground" />
              Job Moderation &amp; Review
            </div>
            <p className="text-xs text-muted-foreground">
              Review draft listings with validation problems, edit job descriptions, or re-parse
              with Cerebras LLM.
            </p>
          </div>
          <Link to="/admin/jobs" className="mt-4 w-fit">
            <Button size="sm" variant="outline" className="gap-1.5">
              Browse Catalog <ArrowRight className="size-3.5" />
            </Button>
          </Link>
        </Card>

        <Card className="border-border bg-card/60 p-5 flex flex-col justify-between hover:border-emerald-500/30 transition">
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold text-foreground mb-1">
              <UserCog className="size-4 text-emerald-500" />
              User Moderation &amp; Credits
            </div>
            <p className="text-xs text-muted-foreground">
              Manage accounts, inspect detailed candidate profiles, and grant rewrite credits.
            </p>
          </div>
          <Link to="/admin/users" className="mt-4 w-fit">
            <Button size="sm" variant="outline" className="gap-1.5">
              Manage Users <ArrowRight className="size-3.5" />
            </Button>
          </Link>
        </Card>

        <Card className="border-border bg-card/60 p-5 flex flex-col justify-between hover:border-brand/30 transition">
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold text-foreground mb-1">
              <Activity className="size-4 text-brand" />
              Tools &amp; Usage Analytics
            </div>
            <p className="text-xs text-muted-foreground">
              Track usage metrics across AI Resume Tailor, ATS Stealth Booster, Fit Matcher, and Credits.
            </p>
          </div>
          <Link to="/admin/tools" className="mt-4 w-fit">
            <Button size="sm" variant="outline" className="gap-1.5">
              Tools Analytics <ArrowRight className="size-3.5" />
            </Button>
          </Link>
        </Card>
      </div>

      {/* Recent Extractions Table */}
      <Card className="border-border bg-card">
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <div>
            <CardTitle className="text-base font-semibold">
              Recent Job Intelligence Extractions
            </CardTitle>
            <CardDescription className="text-xs">
              Latest parsed postings from high-speed Cerebras LLM extraction pipeline.
            </CardDescription>
          </div>
          <Link to="/admin/jobs">
            <Button variant="ghost" size="sm" className="text-xs">
              View All ({stats?.jobs.total ?? 0})
            </Button>
          </Link>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title &amp; Company</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Model</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Extracted</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {stats?.recentJobs?.length ? (
                stats.recentJobs.map((j: any) => (
                  <TableRow key={j.id}>
                    <TableCell className="font-medium">
                      <div className="flex flex-col">
                        <span className="truncate max-w-[280px] text-foreground">{j.title}</span>
                        <span className="text-xs text-muted-foreground">
                          {j.company || "Unknown"}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-[11px] uppercase">
                        {j.source}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {j.llmModel}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={
                          j.status === "active"
                            ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/30 text-[11px]"
                            : j.status === "draft"
                              ? "bg-amber-500/10 text-amber-500 border-amber-500/30 text-[11px]"
                              : "bg-muted text-muted-foreground text-[11px]"
                        }
                      >
                        {j.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right text-xs text-muted-foreground font-mono">
                      {j.extractedAt ? new Date(j.extractedAt).toLocaleTimeString() : "—"}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} className="py-8 text-center text-muted-foreground text-xs">
                    {isLoading ? "Loading extractions…" : "No recent extractions recorded."}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
